<template>
  <div class="fullpage">
    <!-- 图片加载状态指示器 -->
    <div v-if="!allImagesLoaded" class="loading-overlay">
      <div class="loading-spinner"></div>
      <div class="loading-text">正在加载图片...</div>
      <div class="loading-progress">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: preloadProgress + '%' }"></div>
        </div>
        <div class="progress-text">{{ preloadProgress }}%</div>
      </div>
    </div>

    <!-- 顶部全屏图片：PC端用网站_01.jpg -->
    <div ref="topSection" class="top-img-section">
      <img 
        :src="topImgSrc" 
        alt="顶部大图" 
        class="top-img"
        :class="{ 'img-loaded': topImageLoaded }"
        @load="onImageLoadSuccess('top')"
        @error="onImageError($event.target.src, 'top')"
      />
    </div>

    <!-- 预留后续图片区域 -->
    <div ref="contentSection" class="img-section">
      <img 
        :src="sectionImgSrc" 
        alt="图片11" 
        style="max-width:100vw;max-height:200vh;object-fit:contain;"
        :class="{ 'img-loaded': sectionImageLoaded }"
        @load="onImageLoadSuccess('section')"
        @error="onImageError($event.target.src, 'section')"
      />
    </div>

    <!-- 品牌起源文字区域 - 只在PC端显示 -->
    <div class="brand-origin-section">
      <div class="brand-content">
        <div class="brand-title-area">
          <h2 class="brand-title">{{ t('brand.origin.title') }}</h2>
        </div>
        <div class="brand-text-area">
          <p class="brand-description">{{ t('brand.origin.text') }}</p>
        </div>
      </div>

      <div class="img-section3">
        <div class="contact-content">
          <div class="contact-info">
            <p class="contact-description">{{ t('contact.booking') }}</p>
            <p class="contact-description">{{ t('contact.franchise') }}</p>
            <p class="contact-description">{{ t('contact.franchise.email') }}</p>
          </div>

          <div class="contact-qrcodes">
            <div class="qrcode-item">
              <img 
                src="../assets/公众号.png" 
                alt="官方公众号" 
                class="qrcode-img"
                :class="{ 'img-loaded': qrcodeImagesLoaded.wechat }"
                @load="onImageLoadSuccess('wechat')"
                @error="onImageError('../assets/公众号.png', 'wechat')"
              />
              <div class="qrcode-label">{{ t('qrcode.wechat') }}</div>
            </div>
            <div class="qrcode-item">
              <img 
                src="../assets/加盟申请.png" 
                alt="加盟申请" 
                class="qrcode-img"
                :class="{ 'img-loaded': qrcodeImagesLoaded.apply }"
                @load="onImageLoadSuccess('apply')"
                @error="onImageError('../assets/加盟申请.png', 'apply')"
              />
              <div class="qrcode-label">{{ t('qrcode.apply') }}</div>
            </div>
            <div class="qrcode-item">
              <img 
                src="../assets/店长招聘.png" 
                alt="店长招聘" 
                class="qrcode-img"
                :class="{ 'img-loaded': qrcodeImagesLoaded.hire }"
                @load="onImageLoadSuccess('hire')"
                @error="onImageError('../assets/店长招聘.png', 'hire')"
              />
              <div class="qrcode-label">{{ t('qrcode.hire') }}</div>
            </div>
            <div class="qrcode-item">
              <img 
                src="../assets/小程序.png" 
                alt="官方小程序" 
                class="qrcode-img"
                :class="{ 'img-loaded': qrcodeImagesLoaded.mini }"
                @load="onImageLoadSuccess('mini')"
                @error="onImageError('../assets/小程序.png', 'mini')"
              />
              <div class="qrcode-label">{{ t('qrcode.mini') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- ICP备案号 -->
    <div class="icp-footer">
      <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer" class="icp-link">
        沪ICP备12026753号
      </a>
    </div>
  </div>
</template>

<script>
import topImgDesktop from '../assets/网站_01.jpg'
import sectionImgDesktop from '../assets/一号素材.png'
import { t } from '../i18n'

export default {
  name: 'HomeViewDesktop',
  data() {
    return {
      topImgSrc: topImgDesktop,
      sectionImgSrc: sectionImgDesktop,
      isScrolling: false,
      scrollTimeout: null,
      lastScrollTop: 0,
      allImagesLoaded: false,
      topImageLoaded: false,
      sectionImageLoaded: false,
      qrcodeImagesLoaded: {
        wechat: false,
        apply: false,
        mini: false,
        hire: false,
      },
      // 图片预加载状态
      preloadProgress: 0,
      totalImages: 5,
      loadedImages: 0,
      // 性能优化
      imageCache: new Map(),
      lazyLoadObserver: null,
    }
  },
  computed: {
    // 移除有问题的t函数，因为t已经在methods中通过导入可用
  },
  mounted() {
    this.initScrollHandlers();
    this.preloadImages();
    this.addResourceHints();
  },
  beforeDestroy() {
    this.cleanupEventListeners();
    this.cleanupResources(); // 清理资源
  },
  methods: {
    // 国际化函数
    t,
    // 初始化滚动处理器
    initScrollHandlers() {
      // PC端滚动处理
      this.desktopScrollHandler = this.throttle(() => {
        if (this.isScrolling) return;
        
        const topImg = document.querySelector('.top-img');
        const firstImgSection = document.querySelector('.img-section');
        if (!topImg || !firstImgSection) return;
        
        const rect = topImg.getBoundingClientRect();
        const imgSectionRect = firstImgSection.getBoundingClientRect();
        const currentScrollTop = window.pageYOffset;
        const isScrollDown = currentScrollTop > (this.lastScrollTop || 0);
        
        // PC端下滑吸附 - 更精确的检测
        if (isScrollDown && rect.top <= 50 && rect.top >= -100) {
          this.smoothScrollToElement(firstImgSection, 'top');
        }
        // PC端上滑吸附 - 更精确的检测
        else if (!isScrollDown && imgSectionRect.top >= -50 && imgSectionRect.top <= 100) {
          this.smoothScrollToTop();
        }
        
        this.lastScrollTop = currentScrollTop;
      }, 16);
      
      // 绑定事件监听器
      window.addEventListener('scroll', this.desktopScrollHandler, { passive: true });
    },
    
    // 清理事件监听器
    cleanupEventListeners() {
      window.removeEventListener('scroll', this.desktopScrollHandler);
    },
    
    // 平滑滚动到元素
    smoothScrollToElement(element, position = 'top') {
      if (this.isScrolling) return;
      
      this.isScrolling = true;
      const rect = element.getBoundingClientRect();
      let targetScrollTop;
      
      if (position === 'top') {
        targetScrollTop = window.pageYOffset + rect.top;
      } else if (position === 'bottom') {
        targetScrollTop = window.pageYOffset + rect.bottom - window.innerHeight;
      } else {
        targetScrollTop = window.pageYOffset + rect.top + rect.height / 2 - window.innerHeight / 2;
      }
      
      this.performSmoothScroll(targetScrollTop);
    },
    
    // 平滑滚动到顶部
    smoothScrollToTop() {
      if (this.isScrolling) return;
      
      this.isScrolling = true;
      this.performSmoothScroll(0);
    },
    
    // 执行平滑滚动
    performSmoothScroll(targetScrollTop) {
      const startScrollTop = window.pageYOffset;
      const distance = targetScrollTop - startScrollTop;
      const duration = Math.min(Math.abs(distance) * 0.6, 600);
      const startTime = performance.now();
      
      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
      
      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeOutCubic(progress);
        
        window.scrollTo(0, startScrollTop + distance * easedProgress);
        
        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        } else {
          setTimeout(() => {
            this.isScrolling = false;
          }, 50);
        }
      };
      
      requestAnimationFrame(animateScroll);
    },
    
    // 节流函数
    throttle(func, limit) {
      let inThrottle;
      return function() {
        const args = arguments;
        const context = this;
        if (!inThrottle) {
          func.apply(context, args);
          inThrottle = true;
          setTimeout(() => inThrottle = false, limit);
        }
      };
    },

    // 预加载图片
    preloadImages() {
      // 重置加载状态
      this.loadedImages = 0;
      this.preloadProgress = 0;
      
      // 创建图片预加载队列
      const imageQueue = [
        { src: this.topImgSrc, type: 'top', priority: 'high' },
        { src: this.sectionImgSrc, type: 'section', priority: 'high' },
        { src: '../assets/公众号.png', type: 'wechat', priority: 'medium' },
        { src: '../assets/加盟申请.png', type: 'apply', priority: 'medium' },
        { src: '../assets/小程序.png', type: 'mini', priority: 'medium' }
      ];

      // 按优先级排序
      const highPriority = imageQueue.filter(img => img.priority === 'high');
      const mediumPriority = imageQueue.filter(img => img.priority === 'medium');

      // 先加载高优先级图片
      highPriority.forEach(img => this.preloadImage(img.src, img.type));
      
      // 延迟加载中等优先级图片，避免阻塞关键资源
      setTimeout(() => {
        mediumPriority.forEach(img => this.preloadImage(img.src, img.type));
      }, 100);
      
      // 添加超时保护，确保进度条不会卡住
      setTimeout(() => {
        if (this.preloadProgress < 100) {
          console.log('图片加载超时，强制完成');
          this.preloadProgress = 100;
          this.allImagesLoaded = true;
          this.hideLoadingOverlay();
        }
      }, 10000); // 10秒超时
    },

    // 预加载单个图片
    preloadImage(src, type) {
      // 检查缓存
      if (this.imageCache.has(src)) {
        this.onImageLoadSuccess(type);
        return;
      }

      const img = new Image();
      
      // 设置图片属性以优化加载
      img.crossOrigin = 'anonymous'; // 允许跨域
      img.decoding = 'async'; // 异步解码
      
      img.onload = () => {
        this.onImageLoadSuccess(type);
        // 缓存图片
        this.imageCache.set(src, img);
      };
      
      img.onerror = () => {
        this.onImageError(src, type);
      };

      // 开始加载
      img.src = src;
    },

    // 图片加载成功回调
    onImageLoadSuccess(type) {
      this.loadedImages++;
      // 确保进度不超过100%
      this.preloadProgress = Math.min(Math.round((this.loadedImages / this.totalImages) * 100), 100);
      
      // 添加调试日志
      console.log(`图片加载成功: ${type}, 已加载: ${this.loadedImages}/${this.totalImages}, 进度: ${this.preloadProgress}%`);

      switch (type) {
        case 'top':
          this.topImageLoaded = true;
          break;
        case 'section':
          this.sectionImageLoaded = true;
          break;
        case 'wechat':
          this.qrcodeImagesLoaded.wechat = true;
          break;
        case 'apply':
          this.qrcodeImagesLoaded.apply = true;
          break;
        case 'mini':
          this.qrcodeImagesLoaded.mini = true;
          break;
        case 'hire':
          this.qrcodeImagesLoaded.hire = true;
          break;
      }

      // 强制更新进度显示
      this.$forceUpdate();
      
      this.checkAllImagesLoaded();
    },

    // 图片加载失败回调
    onImageError(src, type) {
      console.error('图片加载失败:', src, type);
      // 标记为已加载以避免无限等待
      this.onImageLoadSuccess(type);
    },

    // 检查所有图片是否加载完成
    checkAllImagesLoaded() {
      const allLoaded = this.topImageLoaded && this.sectionImageLoaded &&
                        this.qrcodeImagesLoaded.wechat && this.qrcodeImagesLoaded.apply && this.qrcodeImagesLoaded.mini && this.qrcodeImagesLoaded.hire;
      
      if (allLoaded) {
        // 确保进度显示100%
        this.preloadProgress = 100;
        this.allImagesLoaded = true;
        
        // 延迟隐藏加载指示器，让用户看到100%
        setTimeout(() => {
          this.hideLoadingOverlay();
        }, 500);
        
        this.initLazyLoading();
      }
    },

    // 隐藏加载指示器
    hideLoadingOverlay() {
      const overlay = document.querySelector('.loading-overlay');
      if (overlay) {
        overlay.classList.add('hidden');
        setTimeout(() => {
          overlay.style.display = 'none';
        }, 500);
      }
    },

    // 初始化懒加载
    initLazyLoading() {
      // 使用 Intersection Observer API 实现懒加载
      if ('IntersectionObserver' in window) {
        this.lazyLoadObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target;
              if (img.dataset.src) {
                img.src = img.dataset.src;
                img.removeAttribute('data-src');
                this.lazyLoadObserver.unobserve(img);
              }
            }
          });
        }, {
          rootMargin: '50px 0px', // 提前50px开始加载
          threshold: 0.1
        });
      }
    },

    // 添加资源预加载提示
    addResourceHints() {
      // 创建预加载链接
      const preloadLinks = [
        { href: this.topImgSrc, as: 'image', type: 'image/jpeg' },
        { href: this.sectionImgSrc, as: 'image', type: 'image/png' },
        { href: '../assets/公众号.png', as: 'image', type: 'image/png' },
        { href: '../assets/加盟申请.png', as: 'image', type: 'image/png' },
        { href: '../assets/小程序.png', as: 'image', type: 'image/png' }
      ];

      preloadLinks.forEach(link => {
        const linkElement = document.createElement('link');
        linkElement.rel = 'preload';
        linkElement.href = link.href;
        linkElement.as = link.as;
        if (link.type) {
          linkElement.type = link.type;
        }
        document.head.appendChild(linkElement);
      });
    },

    // 清理资源
    cleanupResources() {
      if (this.lazyLoadObserver) {
        this.lazyLoadObserver.disconnect();
      }
      this.imageCache.clear();
      
      // 清理预加载链接
      const preloadLinks = document.querySelectorAll('link[rel="preload"]');
      preloadLinks.forEach(link => {
        if (link.href.includes('网站_01.jpg') || 
            link.href.includes('一号素材.png') ||
            link.href.includes('公众号.png') ||
            link.href.includes('加盟申请.png') ||
            link.href.includes('小程序.png')) {
          link.remove();
        }
      });
    },
  },
}
</script>

<style scoped>
html, body {
  margin: 0;
  padding: 0;
  background-color: rgb(255, 248, 237);
}

.fullpage {
  width: 100%;
  min-height: 100vh;
  margin: 0;
  padding: 0;
  overflow-x: hidden;
  background: rgb(255, 248, 237);
  scroll-behavior: smooth;
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
}

.top-img {
  width: 100vw;
  height: 100vh;
  object-fit: cover;
  object-position: center bottom;
  display: block;
  margin-left: calc(-50vw + 50%);
  margin-right: calc(-50vw + 50%);
}

.img-section {
  width: 100%;
  min-height: 200px;
  background-color: rgb(255, 248, 237);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

.img-section3 {
  width: 100%;
  min-height: 200px;
  background-color: rgb(255, 248, 237);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

.img-section3 .contact-content {
  display: flex;
  align-items: center;
  gap: 80px;
  border: none;
  outline: none;
  padding: 0 13px;
  width: 90vw;
  margin: 0 auto;
}

.img-section:last-child,
.img-section3:last-child {
  margin-bottom: 0;
}

.img-section img {
  width: 100%;
  height: auto;
  object-fit: cover;
  display: block;
  max-height: 80vh;
}

.img-section3 img {
  width: 100%;
  height: auto;
  object-fit: cover;
  display: block;
}

/* 联系信息区域样式 */
.contact-info {
  flex: 1;
  margin-left: 0;
  margin-right: 0;
}

.contact-qrcodes {
  display: flex;
  gap: 40px;
  align-items: center;
}

.contact-qrcodes .qrcode-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.contact-qrcodes .qrcode-img {
  width: 120px;
  height: 120px;
  object-fit: contain;
  margin-bottom: -7px;
}

/* 二维码标签样式 - 中文 */
.contact-qrcodes .qrcode-label {
  font-size: 13px;
  color: #666;
  font-family: Arial, 'PingFang SC', 'Microsoft YaHei', sans-serif;
  font-weight: 400;
  letter-spacing: 1px;
}

/* 二维码标签样式 - 英文 */
html[lang="en"] .contact-qrcodes .qrcode-label {
  letter-spacing: 0.5px;
  font-family: Arial, sans-serif;
  white-space: nowrap;
}

/* PC端二维码与文字的基准居中 */
.img-section3 .contact-qrcodes .qrcode-item {
  align-items: center;
  text-align: center;
}

.img-section3 .contact-qrcodes .qrcode-item:first-child,
.img-section3 .contact-qrcodes .qrcode-item:last-child {
  margin-left: 0 !important;
  margin-right: 0 !important;
}

/* 联系描述样式 - 中文 */
.contact-description {
  color: #333333;
  font-size: 18px;
  line-height: 1.6;
  margin-bottom: 8px;
  letter-spacing: 1.5px;
  font-family: Arial, 'PingFang SC', 'Microsoft YaHei', sans-serif;
  font-weight: 400;
  white-space: pre-wrap;
  text-align: justify;
  
}

/* 联系描述样式 - 英文 */
html[lang="en"] .contact-description {
  font-family: Arial, sans-serif;
  line-height: 1.5;
  word-spacing: 0px;
  max-width: 100%;
  text-align: left;
  font-size: 13px;
  white-space: nowrap;
}

/* 品牌起源区域样式 */
.brand-origin-section {
  width: 100%;
  height: 100%;
  background-color: rgb(255, 248, 237);
  box-sizing: border-box;
  border: none;
  outline: none;
  position: static;
}

.brand-content {
  display: flex;
  align-items: center;
  width: 90vw;
  margin: 50px auto;
  gap: 80px;
}

.brand-title-area {
  flex: 0 0 250px;
  text-align: right;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 20px;
}

.brand-text-area {
  flex: 1;
  text-align: left;
}

/* 品牌标题样式 - 中文 */
.brand-title {
  color: #333333;
  font-size: 52px;
  font-weight: normal;
  letter-spacing: 6px;
  font-family: Arial, 'PingFang SC', 'Microsoft YaHei', sans-serif;
  margin: 0;
  line-height: 1.2;
}

/* 品牌标题样式 - 英文 */
html[lang="en"] .brand-title {
  letter-spacing: 4px;
  font-family: Arial, sans-serif;
  font-size: 48px;
  font-weight: 600;
  writing-mode: horizontal-tb;
  text-orientation: mixed;
  white-space: nowrap;
  word-spacing: 8px;
}

/* 品牌描述样式 - 中文 */
.brand-description {
  color: #333333;
  font-size: 22px;
  line-height: 1.8;
  letter-spacing: 6px;
  font-family: Arial, 'PingFang SC', 'Microsoft YaHei', sans-serif;
  text-align: justify;
  margin: 0;
  white-space: pre-wrap;
}

/* 品牌描述样式 - 英文 */
html[lang="en"] .brand-description {
  letter-spacing: 0px;
  font-family: Arial, sans-serif;
  line-height: 1.6;
  font-size: 22px;
  word-spacing: 4px;
}

/* PC端图片样式 */
@media (min-width: 1024px) {
  .top-img {
    max-width: 100vw;
    object-fit: cover;
    object-position: center bottom;
  }
  
  /* 品牌标题 - 中文 */
  .brand-title {
    font-size: 52px;
  }
  
  /* 品牌标题 - 英文 */
  html[lang="en"] .brand-title {
    font-size: 48px;
  }
  
  /* 联系描述 - 中文 */
  .contact-description {
    font-size: 18px;
  }
  
  /* 联系描述 - 英文 */
  html[lang="en"] .contact-description {
    font-size: 18px;
  }
}

/* ICP备案号样式 */
.icp-footer {
  width: 100%;
  text-align: center;
  padding: 20px 0;
  background-color: rgb(255, 248, 237);
  border-top: 1px solid #e8e8e8;
}

.icp-link {
  color: #666;
  text-decoration: none;
  font-size: 14px;
  font-family: Arial, 'PingFang SC', 'Microsoft YaHei', sans-serif;
  transition: color 0.3s ease;
}

.icp-link:hover {
  color: #333;
  text-decoration: underline;
}

/* ICP备案号样式 - 英文 */
html[lang="en"] .icp-link {
  font-family: Arial, sans-serif;
  font-size: 13px;
}

/* 加载状态指示器样式 */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: 1;
  transition: opacity 0.5s ease-in-out;
}

.loading-overlay.hidden {
  opacity: 0;
  pointer-events: none;
}

.loading-spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

.loading-text {
  font-size: 18px;
  color: #555;
  font-family: Arial, 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

.loading-progress {
  display: flex;
  align-items: center;
  margin-top: 20px;
  width: 80%;
  max-width: 300px;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background-color: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #3498db;
  border-radius: 4px;
  transition: width 0.3s ease-in-out;
}

.progress-text {
  margin-left: 10px;
  font-size: 16px;
  color: #555;
  font-family: Arial, 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* 图片加载状态样式 */
.top-img,
.img-section img,
.qrcode-img {
  opacity: 0;
  transition: opacity 1.2s cubic-bezier(0.4, 0, 0.2, 1); /* 更长的渐变时间，使用更平滑的缓动函数 */
  filter: blur(2px); /* 初始模糊效果 */
  transform: scale(0.98); /* 初始轻微缩小 */
}

.top-img.img-loaded,
.img-section img.img-loaded,
.qrcode-img.img-loaded {
  opacity: 1;
  filter: blur(0px); /* 加载完成后清晰 */
  transform: scale(1); /* 加载完成后正常大小 */
}

/* 图片性能优化 */
.top-img,
.img-section img,
.qrcode-img {
  will-change: opacity, filter, transform; /* 优化GPU加速 */
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
  image-rendering: -webkit-optimize-contrast; /* 优化图片渲染 */
  image-rendering: optimize-contrast;
}

/* 加载完成后的图片优化 */
.img-loaded {
  will-change: auto;
}

/* 添加图片预加载时的占位样式 */
.img-placeholder {
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
  position: relative;
  overflow: hidden;
}

.img-placeholder::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% { left: -100%; }
  100% { left: 100%; }
}
</style>

